/**
 * An answer as its card (the Ask page sketch): what kind it is and whether its numbers were
 * checked, the headline (typed in when it has just arrived), its key figures, the text, a query's
 * rows as a table, then what it is based on, its queries on request, the pages that show it, and
 * the questions to ask next (or, asking back, the choices).
 *
 * Everything is the backend's checked answer (routes/ask.js): the table's cells are the query's
 * rows as the database gave them, only written in the reader's language here.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { cx } from '@/kit/cx'
import type { AskAnswer, AskNumberCheck, AskStep } from './askApi'
import { AnswerTable } from './AnswerTable'
import { makeAskFormats } from './format'
import { Mark } from './Mark'

const BOLD = /(\*\*[^*\n]+\*\*)/g

/** **bold** as <strong>; everything else plain text (React escapes it), a dangling ** dropped. */
function Rich({ text }: { text: string }) {
    const marks = text.match(/\*\*/g)?.length ?? 0
    const even = marks % 2 === 0 ? text : text.slice(0, text.lastIndexOf('**')) + text.slice(text.lastIndexOf('**') + 2)
    return (
        <>
            {even.split(BOLD).map((part, i) =>
                part.length > 4 && part.startsWith('**') && part.endsWith('**') ? (
                    <strong key={i} className="font-[600] text-[var(--text-default)]">
                        {part.slice(2, -2)}
                    </strong>
                ) : (
                    part
                ),
            )}
        </>
    )
}

/** Answers already typed in: one shown again (its thread opened later) is shown whole. */
const typedIn = new WeakSet<AskAnswer>()

/** The text typed in over about half a second, when `animate`; all of it otherwise. */
function useTyped(text: string, animate: boolean): string {
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const typing = animate && !reduce
    const [typed, setTyped] = useState<{ text: string; n: number }>({ text, n: typing ? 0 : text.length })
    useEffect(() => {
        if (!typing) return undefined
        const perTick = Math.max(2, Math.ceil(text.length / 30))
        const timer = window.setInterval(() => {
            setTyped((current) => {
                const next = Math.min(text.length, (current.text === text ? current.n : 0) + perTick)
                if (next >= text.length) window.clearInterval(timer)
                return { text, n: next }
            })
        }, 16)
        return () => window.clearInterval(timer)
    }, [text, typing])
    const n = !typing ? text.length : typed.text === text ? typed.n : 0
    return text.slice(0, n)
}

export function AnswerCard({
    answer,
    numberCheck,
    steps,
    animate,
    compact = false,
    onAsk,
}: {
    answer: AskAnswer
    numberCheck: AskNumberCheck | null
    steps: AskStep[]
    /** It has just arrived: type the headline in, the rest after it. */
    animate: boolean
    /** The button's panel: no table, the first paragraph only, no queries list. */
    compact?: boolean
    onAsk?: (question: string) => void
}) {
    const { t, i18n } = useTranslation()
    const fmt = makeAskFormats(i18n.language)
    // Taken once, when the card first shows: an answer types in only the first time.
    const [fresh] = useState(() => animate && !typedIn.has(answer))
    useEffect(() => {
        typedIn.add(answer)
    }, [answer])
    const headline = useTyped(answer.headline, fresh)
    const typing = headline.length < answer.headline.length
    const [showQueries, setShowQueries] = useState(false)
    const queries = steps.filter((s) => s.tool === 'query' && s.state === 'done')
    const paragraphs = answer.text.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean)
    const unverified = numberCheck?.unverified.length ?? 0
    const rest = cx(fresh && 'ask-in')
    const later = (i: number) => (fresh ? { animationDelay: `${0.15 + i * 0.12}s` } : undefined)

    return (
        <article className={cx('flex min-w-0 flex-col gap-[14px] rounded-[12px] border border-solid border-[var(--border-default)] bg-[var(--surface-raised)] shadow-[0_1px_2px_rgba(16,16,19,0.04),0_10px_28px_rgba(16,16,19,0.07)]', compact ? 'px-[14px] py-[12px]' : 'px-[22px] pt-[18px] pb-[20px]', fresh && 'ask-in')}>
            <div className="flex flex-wrap items-center justify-between gap-[8px]">
                <span className="inline-flex items-center gap-[6px] rounded-full bg-[var(--accent-weak)] py-[3px] pr-[10px] pl-[8px] text-[12px] leading-[16px] font-[650] text-[var(--accent-default)]">
                    <Mark width={8} />
                    {t(`ask.kind.${answer.kind}`)}
                </span>
                {numberCheck && numberCheck.checked > 0 && !typing && (
                    <span className={cx('inline-flex items-center gap-[4px] text-[12px] leading-[16px]', unverified ? 'text-[var(--text-warning)]' : 'text-[var(--text-success)]', rest)} data-testid="ask-check">
                        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            {unverified ? <path d="M12 8v5M12 17h.01" /> : <path d="M5 12l5 5 9-10" />}
                        </svg>
                        {unverified ? t('ask.check.unverified', { count: unverified }) : t('ask.check.ok')}
                    </span>
                )}
            </div>
            <h2 className={cx('m-0 font-[600] tracking-[-0.01em] text-[var(--text-default)]', compact ? 'text-[16px] leading-[22px]' : 'text-[20px] leading-[27px]')}>
                {headline}
                {typing && <span className="ask-caret">|</span>}
            </h2>
            {!typing && (
                <>
                    {answer.figures.length > 0 && (
                        <div className={cx('flex flex-wrap gap-[6px]', rest)} style={later(0)}>
                            {answer.figures.map((figure) => (
                                <span key={figure} className="rounded-[6px] bg-[var(--surface-header)] px-[9px] py-[4px] text-[12.5px] leading-[16px] font-[650] text-[var(--text-default)] tabular-nums">
                                    {figure}
                                </span>
                            ))}
                        </div>
                    )}
                    {!compact && answer.table && (
                        <div className={cx('overflow-x-auto rounded-[8px] border border-solid border-[var(--border-default)]', rest)} style={later(1)}>
                            <AnswerTable table={answer.table} fmt={fmt} />
                            {answer.table.more > 0 && <div className="border-t border-solid border-[var(--border-default)] px-[10px] py-[6px] text-[12px] text-[var(--text-weaker)]">{t('ask.moreRows', { count: answer.table.more })}</div>}
                        </div>
                    )}
                    {(compact ? paragraphs.slice(0, 1) : paragraphs).map((p, i) => (
                        <p key={i} className={cx('m-0 text-[14px] leading-[21px] font-normal text-[var(--text-weak)]', rest)} style={later(2)}>
                            <Rich text={p} />
                        </p>
                    ))}
                    <div className={cx('flex flex-wrap items-center justify-between gap-[10px]', rest)} style={later(3)}>
                        <span className="text-[12px] leading-[16px] font-normal text-[var(--text-weaker)]">{queries.length > 0 ? t('ask.basedOn', { count: queries.length }) : ''}</span>
                        <div className="flex flex-wrap gap-[8px]">
                            {!compact && queries.some((q) => q.sql) && (
                                <button
                                    type="button"
                                    onClick={() => setShowQueries((v) => !v)}
                                    aria-expanded={showQueries}
                                    className="press inline-flex h-[36px] cursor-pointer items-center rounded-[8px] border-0 bg-[var(--surface-raised)] px-[12px] text-[14px] font-[550] text-[var(--text-default)] shadow-[var(--shadow-button-secondary)] hover:shadow-[var(--shadow-button-secondary-hover)]"
                                >
                                    {showQueries ? t('ask.hideQueries') : t('ask.showQueries')}
                                </button>
                            )}
                            {answer.links.map((link, i) => (
                                <Link
                                    key={link.url}
                                    to={link.url}
                                    className={cx(
                                        'press inline-flex h-[36px] items-center gap-[6px] rounded-[8px] px-[14px] text-[14px] font-[600] no-underline',
                                        // In the button's panel "Open in full view" is the main button.
                                        i === 0 && !compact ? '[background:var(--button-primary-fill)] text-[var(--text-on-accent)] shadow-[var(--shadow-button-primary)]' : 'bg-[var(--surface-raised)] text-[var(--text-default)] shadow-[var(--shadow-button-secondary)]',
                                    )}
                                >
                                    {link.title}
                                    <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M5 12h14M13 6l6 6-6 6" />
                                    </svg>
                                </Link>
                            ))}
                        </div>
                    </div>
                    {showQueries && (
                        <ol className="ask-in m-0 flex flex-col gap-[8px] rounded-[8px] border border-solid border-[var(--border-default)] bg-[var(--surface-stripe)] py-[12px] pr-[14px] pl-[32px] text-[12.5px] leading-[18px]">
                            {queries.map((q) => (
                                <li key={q.id}>
                                    <span className="font-[600] text-[var(--text-default)]">{q.purpose}</span>{' '}
                                    <span className="font-normal text-[var(--text-weaker)]">
                                        · {(q.views ?? []).join(', ')} · {t('ask.count.rows', { count: q.rows ?? 0 })}
                                    </span>
                                    {q.sql && <code className="mt-[3px] block font-mono text-[11.5px] font-normal whitespace-pre-wrap text-[var(--text-weak)]">{q.sql}</code>}
                                </li>
                            ))}
                        </ol>
                    )}
                    {answer.options.length > 0 && onAsk && (
                        <div className={cx('flex flex-wrap items-center gap-[8px]', rest)} style={later(4)}>
                            <span className="mr-[2px] text-[12px] font-normal text-[var(--text-weaker)]">{answer.kind === 'ask_back' ? t('ask.choose') : t('ask.next')}</span>
                            {answer.options.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => onAsk(option)}
                                    className="press min-h-[32px] cursor-pointer rounded-full border border-solid border-[var(--border-strong)] bg-[var(--surface-raised)] px-[12px] py-[5px] text-left text-[13px] leading-[18px] font-[550] text-[var(--text-default)] hover:bg-[var(--interaction-hover)]"
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    )}
                </>
            )}
        </article>
    )
}
